import React, { useState, useEffect } from "react";
import "./Suggestions.css";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useMessengerContext } from "../../hooks/useMessengerContext"; // Import the useSuggestionsContext hook
import { initializeApp, getApps } from "firebase/app";
import { getAuth } from "firebase/auth";
import firebase from "../../Firebase";
import Cookies from "js-cookie";
import { respondToSuggestion } from '../../utils/suggestionResponse';

if (!getApps().length) {
  initializeApp(firebase);
} 

// Each card: Connect / Not interested straight from the card, or "View full
// profile" (onSuggestionProfileClick). onRespond(profile, isInterested) tells
// the page (e.g. the /meet chat, which then asks for the rest of their profile).
const ProfileSuggestions = ({ userIds, communityId, conversationId, onSuggestionProfileClick, onRespond }) => {
  console.log("BASIC TEST: ProfileSuggestions component loaded with userIds:", userIds);
  const [profiles, setProfiles] = useState([]);
  const [loadedUserIds, setLoadedUserIds] = useState(null); // Track which userIds we've loaded
  const { user } = useAuthContext();
  const [csrfToken, setCsrfToken] = useState(null); // New state variable for CSRF token

  const { state, dispatch } = useMessengerContext(); // Use the useSuggestionsContext hook to get state and dispatch

  const {
    userDisplayProfile,
    userInterests
  } = state; // Destructure the needed state variables

  // Connect / Not interested from the card (utils/suggestionResponse.js).
  const [responding, setResponding] = useState(null); // userId being sent
  const respond = async (profile, isInterested) => {
    if (responding) return;
    setResponding(profile.userId);
    try {
      await respondToSuggestion({
        choosingUser: userDisplayProfile || { userId: user.userId },
        consideredUser: profile,
        isInterested,
        communityId: communityId || state.currentConversation?.communityId,
        conversationId: conversationId || state.currentConversation?._id,
      });
      dispatch({ type: 'SET_USER_INTEREST', payload: { userId: profile.userId, isInterested } });
      onRespond?.(profile, isInterested);
    } catch (err) {
      console.error('[suggestions] respond failed:', err.message);
    } finally {
      setResponding(null);
    }
  };

  console.log("BASIC TEST: Current userInterests state:", userInterests);



  // Fetch the CSRF token when the component mounts
  useEffect(() => {
    const fetchCsrfToken = async () => {
      // Fetch the CSRF token from the "mytoken" cookie
      const csrfToken = Cookies.get("mytoken");
      console.log("BASIC TEST: All cookies:", document.cookie);
      console.log("BASIC TEST: Available cookie names:", Object.keys(Cookies.get()));
      console.log("BASIC TEST: CSRF token from mytoken cookie:", csrfToken ? csrfToken : "missing");
      setCsrfToken(csrfToken);
    };

    fetchCsrfToken();
  }, []);

  useEffect(() => {
    // Prevent duplicate fetches for the same userIds
    const userIdsKey = JSON.stringify(userIds?.sort());
    if (loadedUserIds === userIdsKey) {
      console.log("DUPLICATE PREVENTION: Skipping fetch for already loaded userIds:", userIds);
      return;
    }

    const abortController = new AbortController();
    const signal = abortController.signal;

    const fetchProfiles = async () => {
      try {
        console.log("DUPLICATE PREVENTION: Fetching profiles for userIds:", userIds);
        const auth = getAuth();
        const token = await auth.currentUser.getIdToken();

        const requestBody = {
          userIds: userIds,
          communityId: communityId,
        };

        const requestOptions = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-CSRF-Token": csrfToken, // Include the CSRF token here
          },
          body: JSON.stringify(requestBody),
          signal: signal,
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/userProfile/getProfilesToDisplay`,
          requestOptions
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log("DUPLICATE PREVENTION: Successfully fetched profiles:", data.length);
        setProfiles(data);
        setLoadedUserIds(userIdsKey); // Mark these userIds as loaded
      } catch (error) {
        if (error.name === "AbortError") {
          console.log("Fetch aborted:", error);
        } else {
          console.error("Error fetching profiles:", error);
        }
      }
    };

    if (firebase.auth().currentUser && userIds && userIds.length > 0) {
      fetchProfiles();
    }
    
    return () => {
      abortController.abort();
    };
  }, [userIds, communityId, user.userI, csrfToken, loadedUserIds]); // Include loadedUserIds in dependency array


  useEffect(() => {
    // Prevent duplicate interest checks for the same userIds
    const userIdsKey = JSON.stringify(userIds?.sort());
    const lastCheckedKey = `interests_${userIdsKey}`;
    
    // Check if we've already processed these userIds recently (within 5 seconds)
    const lastChecked = sessionStorage.getItem(lastCheckedKey);
    const now = Date.now();
    if (lastChecked && (now - parseInt(lastChecked)) < 5000) {
      console.log("DUPLICATE PREVENTION: Skipping interest check for recently checked userIds:", userIds);
      return;
    }

    const abortController = new AbortController();
    const signal = abortController.signal;
  
    const checkInterests = async () => {
      try {
        console.log("DUPLICATE PREVENTION: checkInterests function called for userIds:", userIds);
        const auth = getAuth();
        const token = await auth.currentUser.getIdToken();

        const requestBody = {
          userId: user.userId, // Assuming user.userI is the userId
          userIds: userIds,
        };
  
        console.log("[profile interested checking] checkInterests useEffect triggered for userIds:", userIds);
        console.log("[profile interested checking] requestBody checkInterests", requestBody);
        const requestOptions = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // Primary authentication via Firebase
            ...(csrfToken && { "X-CSRF-Token": csrfToken }), // Include CSRF token if backend provides it
          },
          body: JSON.stringify(requestBody),
          signal: signal,
        };
  
        console.log("[profile interested checking] Making API call to checkInterests...");
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/interRecord/checkInterests`,
          requestOptions
        );
  
        if (!response.ok) {
          const errorText = await response.text();
          console.error("[profile interested checking] checkInterests API Error:", response.status, errorText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log("[profile interested checking] checkInterests API response:", data);
        console.log("[profile interested checking] userInterestedIn array:", data.userInterestedIn);
        console.log("[profile interested checking] userRejected array:", data.userRejected);
  
        // Dispatch the results to the global MessengerContext state
        dispatch({
          type: 'SET_BULK_USER_INTERESTS',
          payload: data
        });
        
        // Mark these userIds as checked
        sessionStorage.setItem(lastCheckedKey, now.toString());
        
        console.log("[profile interested checking] Dispatched bulk user interests to global state:", data);
      } catch (error) {
        if (error.name === "AbortError") {
          console.log("Fetch aborted:", error);
        } else {
          console.error("Error fetching interests:", error);
        }
      }
    };
  
    // Add null check and prevent duplicate calls
    console.log("BASIC TEST: checkInterests conditions:", {
      hasCurrentUser: !!firebase.auth().currentUser,
      hasUserIds: !!(userIds && userIds.length > 0),
      hasCsrfToken: !!csrfToken,
      userIds: userIds,
      csrfToken: csrfToken ? "present" : "missing"
    });
    
    // Only require Firebase authentication - CSRF token is optional since backend doesn't consistently provide it
    if (firebase.auth().currentUser && userIds && userIds.length > 0) {
      console.log("BASIC TEST: All conditions met, calling checkInterests (authenticated user + userIds)");
      checkInterests();
    } else {
      console.log("BASIC TEST: checkInterests conditions not met - missing:", {
        hasAuth: !!firebase.auth().currentUser,
        hasUserIds: !!(userIds && userIds.length > 0)
      });
    }
  
    return () => {
      abortController.abort();
    };
  }, [userIds, user.userId]); // removed csrfToken dependency since it's not required


  // Legacy handleIntroduceClick function removed - now using SuggestionProfile component for all responses
  return (
    <div key={`suggestions-${JSON.stringify(userIds?.sort())}`}>
      {/* <h1>Suggestions</h1>
      <h3>Are you interested in connecting with:</h3> */}
      {profiles.length > 0 ? (
        profiles.map((profile) => {
          const userInterest = userInterests[profile.userId];
          console.log("[profile interested checking] Rendering profile:", profile.userId, "userInterest:", userInterest, "from userInterests:", userInterests);
          
          return (
            <div
              key={`${profile.id || profile.userId}-${conversationId}`}
              className={`profile ${
                userInterest === 'interested' ? "interconnecting" : 
                userInterest === 'rejected' ? "rejected" : ""
              } suggestion-profile-card`}
              style={{ opacity: userInterest === 'rejected' ? 0.6 : 1 }}
            >
            <div className="profileContent">
              <img
                className="profileImg"
                src={
                  profile.pics[0]
                    ? encodeURI(profile.pics[0])
                    : "https://cdn.pixabay.com/photo/2023/09/22/12/18/profile-8268938_640.png"
                }
                alt={profile.name}
              />
              <div>
                <span className="profileName">{profile.name}</span>
                <span className="profileAge"> {profile.age}</span>
                {profile.milesAway != null && (
                  <p className="profileLocation">📍 {profile.milesAway} {profile.milesAway === 1 ? 'mile' : 'miles'} away</p>
                )}
              </div>
            </div>
            <p className="profileBio">{profile.bio}</p>
            <div className="suggestion-profile-actions">
              {userInterests[profile.userId] === 'interested' ? (
                <div className="interconnecting-status">
                  <span>✓ Interconnecting!</span>
                </div>
              ) : userInterests[profile.userId] === 'rejected' ? (
                <div className="rejected-status">
                  <span>– Not interested</span>
                </div>
              ) : (
                <div className="suggestion-card-buttons">
                  <button type="button" className="suggestion-connect-btn" disabled={!!responding} onClick={() => respond(profile, true)}>
                    {responding === profile.userId ? '…' : 'Connect'}
                  </button>
                  <button type="button" className="suggestion-pass-btn" disabled={!!responding} onClick={() => respond(profile, false)}>
                    Not interested
                  </button>
                </div>
              )}
              {onSuggestionProfileClick && (
                <button type="button" className="suggestion-view-btn" onClick={() => onSuggestionProfileClick(profile)}>
                  View full profile →
                </button>
              )}
            </div>
          </div>
          );
        })
      ) : (
        <p>Loading profiles...</p>
      )}
    </div>
  );
};

export default ProfileSuggestions;
